import { useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { UploadCloud, ChevronRight } from "lucide-react"
import { useLeaseIntakes, useUploadLeaseIntake } from "@/app/api/leaseIntakes"
import { ErrorBanner, formatApiError } from "@/app/components/FormPrimitives"
import type { ClientTheme } from "@/app/lib/themes"
import {
  ACCEPT,
  Card,
  PanelLabel,
  StatusBadge,
  formatWhen,
  mutedText,
  validateIntakeFile,
} from "@/app/pages/LeaseIntakePage/shared"

// Upload step: a dropzone (same visuals as the Import page's) plus the recent
// runs list, so an in-progress or unreviewed run can be resumed.
export function UploadPanel({ theme }: { theme: ClientTheme }) {
  const navigate = useNavigate()
  const upload = useUploadLeaseIntake()
  const [fileError, setFileError] = useState<string | null>(null)

  function handleFile(file: File | undefined | null) {
    if (!file || upload.isPending) return
    upload.reset()
    const err = validateIntakeFile(file)
    setFileError(err)
    if (err) return
    upload.mutate(file, { onSuccess: (run) => navigate(`/leases/intake/${run.id}`) })
  }

  return (
    <>
      <Dropzone pending={upload.isPending} onFile={handleFile} theme={theme} />
      {fileError && <ErrorBanner theme={theme} message={fileError} />}
      {upload.isError && <ErrorBanner theme={theme} message={formatApiError(upload.error)} />}
      <RecentRuns theme={theme} />
    </>
  )
}

function Dropzone({
  pending,
  onFile,
  theme,
}: {
  pending: boolean
  onFile: (file: File | undefined | null) => void
  theme: ClientTheme
}) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload a lease document"
      aria-busy={pending}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        onFile(e.dataTransfer.files?.[0])
      }}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      className="rounded-lg p-8 flex flex-col items-center justify-center text-center"
      style={{
        backgroundColor: dragOver ? `${theme.brandPrimary}0C` : theme.bgSurface,
        border: `2px dashed ${dragOver ? theme.brandPrimary : theme.borderColor}`,
        cursor: pending ? "progress" : "pointer",
        transition: "all 150ms ease",
        opacity: pending ? 0.7 : 1,
      }}
    >
      <UploadCloud size={30} style={{ color: theme.brandPrimary, marginBottom: "8px" }} />
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "14px",
          fontWeight: 600,
          color: theme.textPrimary,
        }}
      >
        {pending ? "Uploading…" : "Drop a recorded Assignment, lease, or CSV"}
      </div>
      <div style={{ ...mutedText(theme), marginTop: "4px" }}>
        or click to browse — PDF (up to 22 MB), PNG/JPG (5 MB), CSV, or .docx (32 MB)
      </div>
      <div
        style={{
          ...mutedText(theme, "11px"),
          color: theme.textDim,
          marginTop: "10px",
          lineHeight: 1.5,
          maxWidth: "560px",
        }}
      >
        Leases are extracted into a review grid — nothing reaches the Lease Schedule until you
        commit. Large PDFs can take a few minutes; you can leave this page and resume later.
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ""
        }}
      />
    </div>
  )
}

function RecentRuns({ theme }: { theme: ClientTheme }) {
  const { data: runs, isLoading, isError, error } = useLeaseIntakes()

  return (
    <div className="mt-6">
      <Card theme={theme}>
        <PanelLabel theme={theme}>Recent imports</PanelLabel>
        {isLoading ? (
          <div style={mutedText(theme, "13px")}>Loading…</div>
        ) : isError ? (
          <ErrorBanner theme={theme} message={formatApiError(error)} />
        ) : !runs || runs.length === 0 ? (
          <div style={mutedText(theme, "13px")}>No documents imported yet.</div>
        ) : (
          <ul className="flex flex-col">
            {runs.map((run) => (
              <li key={run.id} style={{ borderTop: `1px solid ${theme.borderColor}` }}>
                <Link
                  to={`/leases/intake/${run.id}`}
                  className="flex items-center gap-3 py-2.5 px-1"
                  style={{ textDecoration: "none" }}
                >
                  <div className="flex-1 min-w-0">
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: theme.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {run.filename}
                    </div>
                    <div style={mutedText(theme, "11px")}>
                      {formatWhen(run.created_at)} · {run.doc_kind.toUpperCase()} · {run.row_count}{" "}
                      row{run.row_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <StatusBadge status={run.status} theme={theme} />
                  <ChevronRight size={14} style={{ color: theme.textDim, flexShrink: 0 }} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
